import { useTranslations } from "next-intl";
import { Heading, Text } from "@/components/ui";
import { PRIVACY_SECTIONS, TERMS_SECTIONS, type LegalDocument } from "@/lib/legal";

/**
 * H4: the Terms of use or the Privacy notice, from the translated text (`legal.terms.*` /
 * `legal.privacy.*`). `{school}` in the text is the school's name. Body paragraphs are separated by
 * blank lines in the message.
 */
export function LegalDocumentText({
  doc,
  schoolName,
  isDraft = false,
  contactEmail = null,
  headingAs = "h1",
}: {
  doc: LegalDocument;
  schoolName: string;
  /** This school's terms are a draft (its settings row), so the banner names the school. */
  isDraft?: boolean;
  /** The school's privacy contact; shown at the end of the Privacy notice when set. */
  contactEmail?: string | null;
  headingAs?: "h1" | "h2";
}) {
  const t = useTranslations(`legal.${doc}`);
  const tLegal = useTranslations("legal");
  const sections = doc === "terms" ? TERMS_SECTIONS : PRIVACY_SECTIONS;
  const values = { school: schoolName };

  return (
    <article data-testid={`legal-${doc}`} className="space-y-6">
      {isDraft ? (
        <p
          role="note"
          className="rounded-md border border-[var(--warning)] bg-[var(--warning-soft)] px-3 py-2 text-sm text-[var(--warning)]"
          data-testid="legal-draft-banner"
        >
          {tLegal("draftBanner", values)}
        </p>
      ) : null}
      <header>
        <Heading as={headingAs} level={headingAs === "h1" ? "page" : "section"}>
          {t("title")}
        </Heading>
        <Text tone="muted" className="mt-2">
          {tLegal("updated", { date: tLegal("updatedDate") })}
        </Text>
        <Text className="mt-4" size="base">
          {t("intro", values)}
        </Text>
      </header>
      {sections.map((id) => (
        <section key={id} aria-labelledby={`legal-${doc}-${id}`}>
          <Heading as={headingAs === "h1" ? "h2" : "h3"} level="card" id={`legal-${doc}-${id}`}>
            {t(`${id}Title`)}
          </Heading>
          {t(`${id}Body`, values)
            .split("\n\n")
            .map((paragraph, i) => (
              <Text key={i} size="base" className="mt-2">
                {paragraph}
              </Text>
            ))}
        </section>
      ))}
      {doc === "privacy" && contactEmail ? (
        <Text size="base" data-testid="legal-privacy-contact">
          {tLegal("privacyContact", values)}{" "}
          <a href={`mailto:${contactEmail}`} className="underline">
            {contactEmail}
          </a>
        </Text>
      ) : null}
    </article>
  );
}
